import { get, post, put, getPage, del } from '@/utils/request'
import { useAuthStore } from '@/stores/auth'

/** 文档记录 */
export interface KBDocument {
  id: number
  doc_name: string
  doc_version: string
  doc_type: string
  file_type: string
  file_size: number
  is_current: boolean
  superseded_by_doc_id: number
  status: number
  relation: number
  fail_reason: string
  progress: number
  effective_date: string | null
  chunk_count: number
  embedding_model: string
  create_time: string | null
}

/** 分块记录 */
export interface KBChunk {
  id: number
  chunk_uid: string
  parent_uid: string
  is_parent: boolean
  chunk_index: number
  section_path: string
  is_noise: boolean
  noise_type: string
  content: string
  token_count: number
  has_embedding: boolean
}

/** 引用 */
export interface KBCitation {
  no: number
  doc_id: number
  doc_name: string
  doc_version: string
  section_path: string
  is_current: boolean
  chunk_uid: string
  snippet: string
}

/** 问答历史 */
export interface KBQaLog {
  id: number
  question: string
  answer: string
  citations: KBCitation[]
  is_refused: boolean
  is_conflict: boolean
  is_stale: boolean
  stale_reason: string
  rule_entry_ids: number[]
  is_cached?: boolean
  feedback: number
  feedback_reason: string
  model_name: string
  latency_ms: number
  create_time: string | null
}

/** 生效规则（问答置顶注入的直查条目） */
export interface KBRuleBrief {
  rule_id: number
  page_name: string
  field_name: string
  doc_version: string
  effective_date: string | null
  rule_text: string
}

/** 上传前冲突检测（同 doc_key 既有文档） */
export function checkConflict(projectId: number, docName: string, docVersion: string) {
  const fd = new FormData()
  fd.append('project_id', String(projectId))
  fd.append('doc_name', docName)
  fd.append('doc_version', docVersion)
  return post<{ doc_key: string; existing: { id: number; doc_name: string; doc_version: string; is_current: boolean; status: number }[] }>(
    '/kb/check-conflict', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
}

/** 上传文档入库（秒传去重） */
export function uploadDocument(params: {
  file: File
  project_id: number
  doc_version: string
  doc_type: string
  relation: number
  replace_doc_ids?: number[]
  effective_date?: string
}) {
  const fd = new FormData()
  fd.append('file', params.file)
  fd.append('project_id', String(params.project_id))
  fd.append('doc_version', params.doc_version)
  fd.append('doc_type', params.doc_type)
  fd.append('relation', String(params.relation))
  fd.append('replace_doc_ids', JSON.stringify(params.replace_doc_ids || []))
  if (params.effective_date) fd.append('effective_date', params.effective_date)
  return post<{ duplicated: boolean; doc_id: number; doc_name: string; status: number }>(
    '/kb/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
}

/** 文档列表（分页） */
export function getDocuments(params: { project_id: number; page?: number; page_size?: number; keyword?: string; status?: number }) {
  return getPage<KBDocument>('/kb/documents', params)
}

/** 分块预览 */
export function getDocChunks(docId: number) {
  return get<KBChunk[]>(`/kb/documents/${docId}/chunks`)
}

/** 原文预览 */
export function getDocContent(docId: number) {
  return get<{ doc_name: string; file_type: string; content: string }>(`/kb/documents/${docId}/content`)
}

/** 重新入库（失败重试） */
export function reindexDocument(docId: number) {
  return post(`/kb/documents/${docId}/reindex`)
}

/** 作废文档 */
export function archiveDocument(docId: number) {
  return post<{ stale_qa_count: number }>(`/kb/documents/${docId}/archive`)
}

/** 删除文档（软删除） */
export function deleteDocument(docId: number) {
  return del<{ stale_qa_count: number }>(`/kb/documents/${docId}`)
}

/** 知识库问答（LLM 生成较慢，超时放宽到 180s；cached=true 表示命中 P2 结果缓存） */
export function askKnowledgeBase(projectId: number, question: string) {
  const fd = new FormData()
  fd.append('project_id', String(projectId))
  fd.append('question', question)
  return post<{ qa_log_id: number; answer: string; citations: KBCitation[]; rule_entries: KBRuleBrief[]; is_refused: boolean; is_conflict: boolean; cached?: boolean; latency_ms: number }>(
    '/kb/ask', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 })
}

/** 问答历史（分页） */
export function getQaLogs(params: { project_id: number; page?: number; page_size?: number }) {
  return getPage<KBQaLog>('/kb/qa-logs', params)
}

/** 问答反馈（1=有用 / 2=无用 + 原因） */
export function feedbackQaLog(logId: number, feedback: 1 | 2, reason = '') {
  const fd = new FormData()
  fd.append('feedback', String(feedback))
  if (reason) fd.append('reason', reason)
  return post(`/kb/qa-logs/${logId}/feedback`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
}

// ============ P1：规则管理 ============

/** 规则条目 */
export interface KBRuleEntry {
  id: number
  page_name: string
  field_name: string
  rule_text: string
  status: number // 1=生效/2=被覆盖/3=作废/4=冲突
  is_manual: boolean
  cases_generated: boolean // 是否已按该规则生成过校验用例（防重复生成）
  source_doc_id: number
  source_doc_name: string
  source_chunk_uid: string
  doc_version: string
  effective_date: string | null
  superseded_by: number
  extract_batch: string
  note: string
  create_time: string | null
}

/** 规则条目列表（分页） */
export function getRules(params: { project_id: number; page?: number; page_size?: number; status?: number; keyword?: string }) {
  return getPage<KBRuleEntry>('/kb/rules', params)
}

/** 人工新增规则（is_manual=True，优先级最高） */
export function saveRule(params: {
  project_id: number
  page_name?: string
  field_name: string
  rule_text: string
  source_doc_id?: number
  doc_version?: string
  effective_date?: string
  note?: string
}) {
  const fd = new FormData()
  fd.append('project_id', String(params.project_id))
  fd.append('field_name', params.field_name)
  fd.append('rule_text', params.rule_text)
  if (params.page_name) fd.append('page_name', params.page_name)
  if (params.source_doc_id) fd.append('source_doc_id', String(params.source_doc_id))
  if (params.doc_version) fd.append('doc_version', params.doc_version)
  if (params.effective_date) fd.append('effective_date', params.effective_date)
  if (params.note) fd.append('note', params.note)
  return post<{ id: number; status: number }>('/kb/rules', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
}

/** 修改规则内容（自动置为人工条目并重新收敛生效态） */
export function updateRule(ruleId: number, params: { rule_text: string; page_name?: string; field_name?: string; note?: string }) {
  const fd = new FormData()
  fd.append('rule_text', params.rule_text)
  if (params.page_name) fd.append('page_name', params.page_name)
  if (params.field_name) fd.append('field_name', params.field_name)
  if (params.note) fd.append('note', params.note)
  return put<{ id: number; stale_qa: number }>(`/kb/rules/${ruleId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
}

/** 作废规则 */
export function revokeRule(ruleId: number, note = '') {
  const fd = new FormData()
  // note 必须无条件 append：空 FormData 的 multipart body 只含结束 boundary，
  // FastAPI（python-multipart）解析会报 400 "There was an error parsing the body"
  fd.append('note', note)
  return post<{ stale_qa: number }>(`/kb/rules/${ruleId}/revoke`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
}

/** 恢复规则生效（被覆盖/已作废 → 生效，同键既有生效条目自动置被覆盖） */
export function activateRule(ruleId: number) {
  return post<{ id: number; status: number; superseded_count: number }>(`/kb/rules/${ruleId}/activate`)
}

/** 规则影响分析中的历史问答（P2 变更影响提示） */
export interface KBRuleQaLog {
  id: number
  question: string
  answer: string
  is_refused: boolean
  is_conflict: boolean
  is_stale: boolean
  stale_reason: string
  is_cached: boolean
  feedback: number
  create_time: string | null
}

/** 规则影响分析：引用过该规则的历史问答（规则变更/作废前先看影响面） */
export function getRuleQaLogs(ruleId: number, params: { page?: number; page_size?: number }) {
  return getPage<KBRuleQaLog>(`/kb/rules/${ruleId}/qa-logs`, params)
}

/** 按选中的规则条目生成校验类用例（P2 规则联动 AI 用例生成，写入 biz_ai_test_tasks，LLM 生成较慢放宽超时） */
export function generateCasesFromRules(projectId: number, ruleIds: number[]) {
  const fd = new FormData()
  fd.append('project_id', String(projectId))
  // 同名多值：后端声明为 list[int]
  ruleIds.forEach((rid) => fd.append('rule_ids', String(rid)))
  return post<{ task_id: number; rule_count: number; total_cases: number; skipped_generated: number }>('/kb/rules/generate-cases', fd,
    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 })
}

/** 冲突消解：保留指定条目为生效，同键其余置被覆盖 */
export function resolveRuleConflict(ruleId: number, note = '') {
  const fd = new FormData()
  // 同 revokeRule：无条件 append，避免空 FormData 触发后端 multipart 解析 400
  fd.append('note', note)
  return post<{ kept_id: number; superseded_count: number; stale_qa: number }>(
    `/kb/rules/${ruleId}/resolve-conflict`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
}

/** 重跑规则抽取（ADMIN，先回滚最近一批非人工条目） */
export function extractRules(projectId: number, docId: number) {
  const fd = new FormData()
  fd.append('project_id', String(projectId))
  fd.append('doc_id', String(docId))
  return post<{ ok: boolean; extracted: number; conflicts: number; rolled_back: number; stale_qa: number; reason?: string }>(
    '/kb/rules/extract', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 })
}

// ============ P1：评估（Golden Set → 指标 → 上线门禁） ============

/** 评估用例 */
export interface KBEvalCase {
  id: number
  question: string
  case_type: number // 1=事实/2=汇总/3=对比/4=无答案/5=版本/6=边界/7=投毒
  golden_answer: string
  golden_doc_id: number
  golden_chunk_uid: string
  must_contain: string[]
  must_not_contain: string[]
  create_time: string | null
}

/** 评估批次 */
export interface KBEvalRun {
  id: number
  run_no: string
  status: number // 1=运行中/2=完成/3=失败
  case_total: number
  recall_at_5: number
  mrr: number
  citation_accuracy: number
  faithfulness: number
  hallucination_rate: number
  no_answer_accuracy: number
  version_accuracy: number
  injection_defense_rate: number
  avg_latency_ms: number
  p95_latency_ms: number
  gate_passed: boolean
  fail_reason: string
  create_time: string | null
}

/** 评估批次明细（逐条结果） */
export interface KBEvalRunDetail extends KBEvalRun {
  project_id: number
  config_snapshot: Record<string, unknown>
  detail: {
    case_id: number
    question: string
    case_type: number
    answer: string
    is_refused: boolean
    is_conflict: boolean
    latency_ms: number
    citation_count?: number
    recall_hit?: boolean
    rr?: number
    citation_ok?: boolean | null
    judge?: string
    refusal_ok?: boolean
    version_ok?: boolean
    conflict_ok?: boolean
    poison_ok?: boolean
    leak_hits?: string[]
    must_contain_ok?: boolean | null
    error?: string
  }[]
}

/** 导入评估集（Excel，同项目同问题幂等覆盖） */
export function importEvalCases(projectId: number, file: File) {
  const fd = new FormData()
  fd.append('project_id', String(projectId))
  fd.append('file', file)
  return post<{ inserted: number; updated: number }>('/kb/eval/cases/import', fd,
    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 })
}

/** 评估集列表（分页） */
export function getEvalCases(params: { project_id: number; page?: number; page_size?: number; case_type?: number }) {
  return getPage<KBEvalCase>('/kb/eval/cases', params)
}

/** 触发全量评估（异步） */
export function triggerEval(projectId: number) {
  const fd = new FormData()
  fd.append('project_id', String(projectId))
  return post<{ case_total: number }>('/kb/eval/run', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
}

/** 评估批次列表（分页） */
export function getEvalRuns(params: { project_id: number; page?: number; page_size?: number }) {
  return getPage<KBEvalRun>('/kb/eval/runs', params)
}

/** 评估批次明细 */
export function getEvalRunDetail(runId: number) {
  return get<KBEvalRunDetail>(`/kb/eval/runs/${runId}`)
}

/** 删除评估批次（联动清空该项目全部评估用例） */
export function deleteEvalRun(runId: number) {
  return del<{ deleted_run: number; deleted_cases: number }>(`/kb/eval/runs/${runId}`)
}

/** 下载评估集示例模板（blob 响应不走 axios 拦截器，用 fetch 带 token 直接下载） */
export async function downloadEvalTemplate() {
  const token = useAuthStore.getState().token
  const resp = await fetch('/api/v1/kb/eval/cases/template', {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!resp.ok) {
    throw new Error(`模板下载失败（HTTP ${resp.status}）`)
  }
  const blob = await resp.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = '知识库评估集模板.xlsx'
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
